import { useRef, type CSSProperties, type ReactElement, type ReactNode } from 'react';
import {
    DefaultInfiniteScrollListStateCopy,
    InfiniteScrollListEmptyState,
    InfiniteScrollListErrorState,
    InfiniteScrollListLoadingState,
    type InfiniteScrollListStateCopy,
} from '@/components/ui/infinite-scroll-list-state';
import type { InfiniteScrollListCursor } from '@/components/ui/infinite-scroll-list.types';
import {
    useInfiniteScrollList,
    type UseInfiniteScrollListOptions,
} from '@/hooks/useInfiniteScrollList';

interface EmbeddedInfiniteScrollListRenderContext<
    TItem,
    TCursor extends InfiniteScrollListCursor,
> {
    readonly items: ReadonlyArray<TItem>;
    readonly isFetching: boolean;
    readonly isFetchingNextPage: boolean;
    readonly hasNextPage: boolean;
    readonly pagination: ReturnType<
        typeof useInfiniteScrollList<TItem, TCursor, readonly unknown[]>
    >['pagination'];
}

export interface EmbeddedInfiniteScrollListProps<
    TItem,
    TCursor extends InfiniteScrollListCursor,
    TQueryKeyPrefix extends readonly unknown[],
> extends Pick<
        UseInfiniteScrollListOptions<TItem, TCursor, TQueryKeyPrefix>,
        | 'queryKeyPrefix'
        | 'pageSize'
        | 'fetchPage'
        | 'parseCursorParam'
        | 'initialQueryText'
        | 'enabled'
        | 'retry'
        | 'staleTime'
        | 'gcTime'
        | 'rootMargin'
        | 'threshold'
    > {
    stateCopy?: Partial<InfiniteScrollListStateCopy>;
    maxHeight: CSSProperties['maxHeight'];
    compactBottomSpacing?: boolean;
    renderItems: (
        context: EmbeddedInfiniteScrollListRenderContext<TItem, TCursor>
    ) => ReactNode;
}

export function EmbeddedInfiniteScrollList<
    TItem,
    TCursor extends InfiniteScrollListCursor,
    TQueryKeyPrefix extends readonly unknown[],
>(props: EmbeddedInfiniteScrollListProps<TItem, TCursor, TQueryKeyPrefix>): ReactElement {
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const stateCopy: InfiniteScrollListStateCopy = {
        ...DefaultInfiniteScrollListStateCopy,
        ...props.stateCopy,
    };
    const list = useInfiniteScrollList<TItem, TCursor, TQueryKeyPrefix>({
        queryKeyPrefix: props.queryKeyPrefix,
        pageSize: props.pageSize,
        fetchPage: props.fetchPage,
        parseCursorParam: props.parseCursorParam,
        initialQueryText: props.initialQueryText,
        enabled: props.enabled,
        retry: props.retry,
        staleTime: props.staleTime,
        gcTime: props.gcTime,
        rootMargin: props.rootMargin,
        threshold: props.threshold,
        observerRootRef: scrollContainerRef,
    });

    if (list.error !== null) {
        return (
            <div
                ref={scrollContainerRef}
                data-testid="embedded-scroll-container"
                className="overflow-y-auto"
                style={{ maxHeight: props.maxHeight }}
            >
                <InfiniteScrollListErrorState
                    error={list.error}
                    genericErrorLabel={stateCopy.genericErrorLabel}
                />
            </div>
        );
    }

    if (list.isLoading && list.items.length === 0) {
        return (
            <div
                ref={scrollContainerRef}
                data-testid="embedded-scroll-container"
                className="overflow-y-auto"
                style={{ maxHeight: props.maxHeight }}
            >
                <InfiniteScrollListLoadingState label={stateCopy.loadingLabel} />
            </div>
        );
    }

    if (list.items.length === 0) {
        return (
            <div
                ref={scrollContainerRef}
                data-testid="embedded-scroll-container"
                className="overflow-y-auto"
                style={{ maxHeight: props.maxHeight }}
            >
                <InfiniteScrollListEmptyState label={stateCopy.emptyLabel} />
            </div>
        );
    }

    return (
        <div
            ref={scrollContainerRef}
            data-testid="embedded-scroll-container"
            className="overflow-y-auto"
            style={{ maxHeight: props.maxHeight }}
        >
            {props.renderItems({
                items: list.items,
                isFetching: list.isFetching,
                isFetchingNextPage: list.isFetchingNextPage,
                hasNextPage: list.hasNextPage,
                pagination: list.pagination,
            })}
            <div
                ref={list.loadMoreSentinelRef}
                className={props.compactBottomSpacing ? 'h-0' : 'h-8'}
            />
            {list.isFetching ? (
                <InfiniteScrollListLoadingState label={stateCopy.loadingLabel} />
            ) : null}
        </div>
    );
}
